/**
 * SyncedTranscript — the karaoke bilingual transcript for the Audio Lessons
 * player (AUDIO-LESSONS-004).
 *
 * Subscribes to the live track-player position, binary-searches the baked word
 * timings to highlight the active line (and the active word within it), renders
 * the bilingual pairs (spoken side + translation/gloss), auto-scrolls the active
 * line into view (manual scroll temporarily suspends it), and seeks to a line's
 * `startMs` when tapped.
 *
 * Performance: each line is `React.memo`-wrapped and only the ACTIVE line ever
 * receives a non-`-1` `activeWordIndex`, so a word-level position change
 * re-renders exactly one row, not the whole list (CHAT_PERFORMANCE).
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { FlatList, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import { useAudioProgress } from '../../hooks/useAudioProgress';
import {
  alignTokenTimes,
  findActiveSegmentIndex,
  lastIndexAtOrBefore,
  type AudioTranscript,
  type AudioTranscriptSegment,
} from '../../types/audioTranscript';

/** ms with no manual scroll before auto-scroll resumes. */
const AUTO_SCROLL_RESUME_MS = 4000;

interface SyncedTranscriptProps {
  transcript: AudioTranscript;
  /** The user's source locale (e.g. `es-ES`) — picks the region gloss variant. */
  sourceLocale: string;
  /** Seek the player to an absolute position (ms) when a line is tapped. */
  onSeek: (positionMs: number) => void;
}

/**
 * Resolve a native (source-language) text record to the user's source-region
 * gloss, falling back to the es_419 default then any present value. `sourceKey`
 * is the underscored source locale (e.g. `es_ES`). The native/source side is
 * ALWAYS resolved against the source locale — never lessonLanguage — so a
 * target-language learner still sees the Spanish gloss.
 */
function glossOf(native: Record<string, string>, sourceKey: string): string {
  return native[sourceKey] ?? native.es_419 ?? Object.values(native)[0] ?? '';
}

/** Pick the translation side opposite the spoken side from a narration record. */
function narrationTranslation(
  narration: Record<string, string> | undefined,
  spoken: 'source' | 'target',
  sourceKey: string,
): string {
  if (!narration) return '';
  // Spoken source (es) → show a target (en) translation, and vice-versa.
  const wantPrefix = spoken === 'source' ? 'en' : 'es';
  // When showing the source (es) side, prefer the user's source-region variant.
  if (wantPrefix === 'es' && narration[sourceKey]) return narration[sourceKey];
  for (const [locale, value] of Object.entries(narration)) {
    if (locale.startsWith(wantPrefix) && value) return value;
  }
  return '';
}

/** The bilingual display lines for one segment: the spoken line + its translation. */
function segmentLines(
  segment: AudioTranscriptSegment,
  spoken: 'source' | 'target',
  sourceKey: string,
): { spoken: string; translation: string } {
  if (segment.transcriptPair) {
    // CONTENT: spoken target line + native gloss (source-region aware).
    return { spoken: segment.transcriptPair.target, translation: glossOf(segment.transcriptPair.native, sourceKey) };
  }
  if (segment.vocab && segment.vocab.length > 0) {
    // VOCAB: "term — gloss" pairs on each line.
    const pairs = segment.vocab.map((item) => {
      const gloss = glossOf(item.source, sourceKey);
      return gloss ? `${item.target} — ${gloss}` : item.target;
    });
    return { spoken: pairs.join('   ·   '), translation: '' };
  }
  // NARRATION: spoken side (= segment.text) + the opposite side as translation.
  return { spoken: segment.text, translation: narrationTranslation(segment.narration, spoken, sourceKey) };
}

interface TranscriptLineProps {
  segment: AudioTranscriptSegment;
  index: number;
  spoken: 'source' | 'target';
  /** Underscored source locale key (e.g. `es_ES`) for region-aware glosses. */
  sourceKey: string;
  isActive: boolean;
  /** Active word index WITHIN this line, or -1 (only ever set on the active line). */
  activeWordIndex: number;
  onPress: (index: number) => void;
}

const TranscriptLine = React.memo(function TranscriptLine({
  segment,
  index,
  spoken,
  sourceKey,
  isActive,
  activeWordIndex,
  onPress,
}: TranscriptLineProps) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const handlePress = useCallback(() => onPress(index), [onPress, index]);

  const { spoken: spokenText, translation } = useMemo(
    () => segmentLines(segment, spoken, sourceKey),
    [segment, spoken, sourceKey],
  );
  // Split into display tokens. Only tokens carrying spoken text (a letter or
  // number) advance the word index — pure punctuation (e.g. the vocab "—"/"·"
  // separators) gets -1 — so display tokens line up positionally with the timed
  // Azure words[], which excludes punctuation-only boundaries. Computed
  // functionally (no mutable counter) to satisfy the immutability rule.
  const tokens = useMemo(() => {
    const parts = spokenText.split(/(\s+)/);
    const isSpokenTok = (p: string) => p.length > 0 && /[\p{L}\p{N}]/u.test(p);
    return parts.map((tok, i) => {
      if (!isSpokenTok(tok)) return { tok, wordIndex: -1, isSpace: /^\s+$/.test(tok) };
      const wordIndex = parts.slice(0, i).filter(isSpokenTok).length;
      return { tok, wordIndex, isSpace: false };
    });
  }, [spokenText]);

  return (
    <TouchableOpacity
      testID={`audio-transcript-line-${index}`}
      accessibilityRole="button"
      accessibilityLabel={spokenText}
      onPress={handlePress}
      activeOpacity={0.7}
      style={[styles.line, isActive && { backgroundColor: theme.card }]}
    >
      <Text allowFontScaling={false} style={{ fontSize: 17 * fontScale, lineHeight: 26 * fontScale, ...ffs }}>
        {tokens.map(({ tok, wordIndex, isSpace }, i) => {
          if (isSpace) return <Text key={i}>{tok}</Text>;
          // wordIndex < 0 marks a punctuation-only token — never highlighted and
          // excluded from the "already spoken" shading.
          const isWordActive = isActive && wordIndex >= 0 && wordIndex === activeWordIndex;
          const isSpoken = isActive && wordIndex >= 0 && wordIndex <= activeWordIndex;
          return (
            <Text
              key={i}
              style={{
                color: isWordActive
                  ? theme.primary
                  : isSpoken
                    ? theme.text
                    : isActive
                      ? theme.textSecondary
                      : theme.text,
                fontWeight: isWordActive ? '700' : '400',
              }}
            >
              {tok}
            </Text>
          );
        })}
      </Text>
      {translation.length > 0 && (
        <Text
          allowFontScaling={false}
          style={{ fontSize: 14 * fontScale, color: theme.textSecondary, marginTop: 2, ...ffs }}
        >
          {translation}
        </Text>
      )}
    </TouchableOpacity>
  );
});

export const SyncedTranscript = React.memo(function SyncedTranscript({
  transcript,
  sourceLocale,
  onSeek,
}: SyncedTranscriptProps) {
  const { positionMs } = useAudioProgress();

  const segments = transcript.segments;
  const spoken = transcript.spokenLanguage;
  const sourceKey = useMemo(() => sourceLocale.replace(/-/g, '_'), [sourceLocale]);

  const activeIndex = useMemo(
    () => findActiveSegmentIndex(segments, positionMs),
    [segments, positionMs],
  );
  // Token→time map for the active line, aligned by TEXT to the Azure words (not a
  // blind index), so a stray/garbled word-boundary entry can't desync the
  // highlight. Memoized on the active segment; only the lookup runs per tick.
  const activeTokenTimes = useMemo(() => {
    if (activeIndex < 0) return null;
    const seg = segments[activeIndex];
    const { spoken: text } = segmentLines(seg, spoken, sourceKey);
    const tokens = text.split(/\s+/).filter((tok) => /[\p{L}\p{N}]/u.test(tok));
    return alignTokenTimes(tokens, seg.words, seg.startMs, seg.endMs);
  }, [segments, activeIndex, spoken, sourceKey]);
  const activeWordIndex = useMemo(
    () => (activeTokenTimes ? lastIndexAtOrBefore(activeTokenTimes, positionMs) : -1),
    [activeTokenTimes, positionMs],
  );

  const listRef = useRef<FlatList<AudioTranscriptSegment>>(null);
  const autoScrollSuspendedUntil = useRef(0);

  // Auto-scroll the active line into view unless the user is manually scrolling.
  useEffect(() => {
    if (activeIndex < 0) return;
    if (Date.now() < autoScrollSuspendedUntil.current) return;
    listRef.current?.scrollToIndex({ index: activeIndex, viewPosition: 0.4, animated: true });
  }, [activeIndex]);

  const handleManualScroll = useCallback(() => {
    autoScrollSuspendedUntil.current = Date.now() + AUTO_SCROLL_RESUME_MS;
  }, []);

  const handleLinePress = useCallback(
    (index: number) => onSeek(segments[index]?.startMs ?? 0),
    [onSeek, segments],
  );

  const renderItem = useCallback(
    ({ item, index }: { item: AudioTranscriptSegment; index: number }) => (
      <TranscriptLine
        segment={item}
        index={index}
        spoken={spoken}
        sourceKey={sourceKey}
        isActive={index === activeIndex}
        activeWordIndex={index === activeIndex ? activeWordIndex : -1}
        onPress={handleLinePress}
      />
    ),
    [spoken, sourceKey, activeIndex, activeWordIndex, handleLinePress],
  );

  const keyExtractor = useCallback((s: AudioTranscriptSegment) => String(s.index), []);

  // Variable-height rows: recover from a failed scrollToIndex by nudging then retrying.
  const onScrollToIndexFailed = useCallback(
    (info: { index: number; averageItemLength: number }) => {
      listRef.current?.scrollToOffset({ offset: info.averageItemLength * info.index, animated: false });
      setTimeout(() => {
        if (info.index < segments.length) {
          listRef.current?.scrollToIndex({ index: info.index, viewPosition: 0.4, animated: true });
        }
      }, 80);
    },
    [segments.length],
  );

  return (
    <View testID="audio-transcript" style={styles.container}>
      <FlatList
        ref={listRef}
        data={segments}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        extraData={`${activeIndex}:${activeWordIndex}`}
        onScrollBeginDrag={handleManualScroll}
        onScrollToIndexFailed={onScrollToIndexFailed}
        contentContainerStyle={{ paddingBottom: 24 }}
        showsVerticalScrollIndicator={false}
      />
    </View>
  );
});

const styles = StyleSheet.create({
  container: { flex: 1 },
  line: { paddingVertical: 8, paddingHorizontal: 12, borderRadius: 10, marginBottom: 2 },
});
